import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { ApiError } from '@shared/api/client';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { Alert } from '@shared/ui/Alert';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { RowMenu } from '@shared/ui/rowmenu';
import { StateBadge } from '@shared/ui/StateBadge';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { SurfaceError, TruncationLine } from '../_shared/components';
import { composeCallbackEndpoint, formatFeeFromBasisPoints } from '../_shared/constants';
import { sharedCopy } from '../_shared/copy';
import type { GatewayTestResult, PaymentGateway } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { toGatewayWriteBody, type GatewayFormValues } from './forms.schema';
import { GatewayDialog } from './Gateways.GatewayDialog';
import { RotateDialog } from './Gateways.RotateDialog';
import { TestDialog } from './Gateways.TestDialog';
import {
  useActivateGateway,
  useDeleteGateway,
  useRegisterGateway,
  useRotateGatewayCredential,
  useTestGateway,
  useUpdateGateway,
} from './mutations';
import { paymentGatewaysKey, usePaymentGateways } from './queries';

const TABLE_COLUMNS = 5;

/**
 * `/payment-gateways` — the provider registry, with one provider bound at a
 * time.
 *
 * **No provider is named in this file or in its copy.** The registry's names are
 * strings the operator types; printing a brand would assert a commercial
 * relationship no source in this workspace records.
 *
 * **The credential is never a column, never a field, and never readable.** The
 * name cell says a credential is stored; rotation replaces it; nothing on this
 * surface or any other reads it back.
 *
 * **Activation is confirmed and the confirmation names both sides.** It is not
 * destructive, so the ordinary rule for a row action does not reach it — but it
 * re-points the route every payment travels and takes the incumbent out of
 * service in the same instant, and a sentence naming only the incoming provider
 * hides that something left.
 *
 * **Deleting the bound provider is barred rather than warned about**, because
 * removing it would leave the checkout hand-off with no target. The entry stays
 * visible and disabled with the remedy beside it: hiding it would leave two rows
 * with menus of different lengths and no explanation, which reads as a fault
 * rather than as a rule.
 */
export function GatewaysPage() {
  const query = usePaymentGateways();
  const queryClient = useQueryClient();
  const toast = useToast();

  const registerGateway = useRegisterGateway();
  const updateGateway = useUpdateGateway();
  const deleteGateway = useDeleteGateway();
  const activateGateway = useActivateGateway();
  const testGateway = useTestGateway();
  const rotateCredential = useRotateGatewayCredential();

  const [recordOpen, setRecordOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentGateway | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);

  const [rotating, setRotating] = useState<PaymentGateway | null>(null);
  const [rotateError, setRotateError] = useState<string | null>(null);

  const [testing, setTesting] = useState<PaymentGateway | null>(null);
  const [testResult, setTestResult] = useState<GatewayTestResult | null>(null);

  const [confirmSubject, setConfirmSubject] = useState<ConfirmSubject | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PaymentGateway | null>(null);
  const [pendingActivate, setPendingActivate] = useState<PaymentGateway | null>(null);

  const gateways = query.data?.items ?? [];
  const active = gateways.find((gateway) => gateway.is_active) ?? null;

  function failureMessage(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;
    return resolveErrorCopy(code, status, errors).message;
  }

  function submitRecord(values: GatewayFormValues) {
    setRecordError(null);

    const body = toGatewayWriteBody(values);
    if (!body) {
      setRecordError(copy.validation.callbackInvalid);
      return;
    }

    const onError = (error: unknown) => setRecordError(failureMessage(error));

    if (editing) {
      updateGateway.mutate(
        { gatewayId: editing.id, body },
        {
          onSuccess: () => {
            setRecordOpen(false);
            toast.show(copy.toast.updated, 'success');
          },
          onError,
        },
      );
      return;
    }

    registerGateway.mutate(body, {
      onSuccess: () => {
        setRecordOpen(false);
        toast.show(copy.toast.registered, 'success');
      },
      onError,
    });
  }

  function commitRotation(value: string) {
    const gateway = rotating;
    if (!gateway) return;

    setRotateError(null);
    rotateCredential.mutate(
      { configurationKey: gateway.credential_configuration_key, value },
      {
        onSuccess: () => {
          setRotating(null);
          toast.show(copy.toast.rotated, 'success');
        },
        onError: (error) => setRotateError(failureMessage(error)),
      },
    );
  }

  function askActivate(gateway: PaymentGateway) {
    setPendingActivate(gateway);
    setConfirmSubject({
      heading: copy.activate.heading,
      body: active
        ? copy.activate.bodyWithOutgoing(gateway.display_name, active.display_name)
        : copy.activate.bodyFirst(gateway.display_name),
      commitLabel: copy.activate.commit,
      // Reversible-but-consequential: it re-points the money route and takes the
      // incumbent out of service, and neither is a destruction.
      intent: 'warning',
    });
  }

  function askDelete(gateway: PaymentGateway) {
    setPendingDelete(gateway);
    setConfirmSubject({
      heading: copy.confirm.heading(gateway.display_name),
      body: copy.confirm.body,
      commitLabel: copy.confirm.commit,
      intent: 'danger',
    });
  }

  function closeConfirm() {
    setConfirmSubject(null);
    setPendingDelete(null);
    setPendingActivate(null);
  }

  function commitConfirm() {
    if (pendingActivate) {
      const gateway = pendingActivate;
      activateGateway.mutate(gateway.id, {
        onSuccess: () => {
          closeConfirm();
          toast.show(copy.toast.activated(gateway.display_name), 'success');
        },
        onError: (error) => {
          closeConfirm();
          toast.show(failureMessage(error), 'danger');
        },
      });
      return;
    }

    if (pendingDelete) {
      deleteGateway.mutate(pendingDelete.id, {
        onSuccess: () => {
          closeConfirm();
          toast.show(copy.toast.deleted, 'success');
        },
        onError: (error) => {
          closeConfirm();
          toast.show(failureMessage(error), 'danger');
        },
      });
    }
  }

  function openTest(gateway: PaymentGateway) {
    setTesting(gateway);
    setTestResult(null);
  }

  function runTest() {
    const gateway = testing;
    if (!gateway) return;

    testGateway.mutate(gateway.id, {
      // A failing probe is a `200` carrying `ok: false` and lands here, not in
      // `onError`; `onError` is a request that never reached the provider.
      onSuccess: (result) => setTestResult(result),
      onError: (error) =>
        setTestResult({ ok: false, latency_ms: null, provider_message: failureMessage(error) }),
    });
  }

  return (
    <>
      <Breadcrumb items={[{ label: sharedCopy.crumbs.console, to: '/' }, { label: copy.crumb }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      {/* A statement, not an error — it carries no alert role and no danger colour. */}
      <Alert kind="info" title={copy.constraint.title} className="mb-6">
        {copy.constraint.body}
      </Alert>

      <section aria-labelledby="gateways-heading">
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 id="gateways-heading" className="font-display text-h2 font-bold">
            {copy.section.heading}
          </h2>

          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setRecordError(null);
              setRecordOpen(true);
            }}
            className="hit-target ms-auto flex items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay"
          >
            <PlusGlyph />
            {copy.section.add}
          </button>
        </div>

        <p className="mb-4 max-w-measure text-xs text-ink-muted">{copy.namingNote}</p>

        {query.isError ? (
          <SurfaceError
            error={query.error}
            catalog={errors}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: paymentGatewaysKey() })}
          />
        ) : null}

        {query.isPending ? (
          <>
            <p role="status" className="sr-only">
              {copy.states.loading}
            </p>
            <GatewayTable>
              <RowSkeleton columns={TABLE_COLUMNS} />
            </GatewayTable>
          </>
        ) : null}

        {query.data ? (
          gateways.length === 0 ? (
            <EmptyState message={copy.states.empty} />
          ) : (
            <>
              <GatewayTable>
                {gateways.map((gateway) => (
                  <tr key={gateway.id}>
                    <DTableRowHeader>
                      <span className="block">{gateway.display_name}</span>
                      <span className="mt-1 block text-xs text-ink-muted">{copy.credentialNote}</span>
                    </DTableRowHeader>

                    <td>
                      <StateBadge kind={gateway.is_active ? 'success' : 'neutral'}>
                        {gateway.is_active ? copy.state.active : copy.state.inactive}
                      </StateBadge>
                    </td>

                    <td className="whitespace-nowrap">
                      <bdi>{formatFeeFromBasisPoints(gateway.fee_bps)}</bdi>
                    </td>

                    {/*
                      A long unbroken Latin address otherwise sets the column to
                      its own width and crushes the four beside it. `anywhere`
                      rather than `break-all`: it breaks only where it must, so
                      the host and the path stay legible.
                    */}
                    <td className="[max-inline-size:22rem] [overflow-wrap:anywhere]">
                      <bdi className="mono">
                        {composeCallbackEndpoint(gateway.base_url, gateway.callback_path)}
                      </bdi>
                    </td>

                    <td className="w-[3.5rem]">
                      <RowMenu
                        label={copy.rowMenu.label(gateway.display_name)}
                        hint={gateway.is_active ? copy.rowMenu.activeHint : undefined}
                        entries={[
                          {
                            key: 'edit',
                            label: copy.rowMenu.edit,
                            onSelect: () => {
                              setEditing(gateway);
                              setRecordError(null);
                              setRecordOpen(true);
                            },
                          },
                          {
                            key: 'rotate',
                            label: copy.rowMenu.rotate,
                            // Rotation voids the live key and the old value is
                            // not recoverable, so it takes the same weight as a
                            // removal inside the panel.
                            danger: true,
                            onSelect: () => {
                              setRotateError(null);
                              setRotating(gateway);
                            },
                          },
                          {
                            key: 'test',
                            label: copy.rowMenu.test,
                            onSelect: () => openTest(gateway),
                          },
                          {
                            key: 'activate',
                            label: copy.rowMenu.activate,
                            disabled: gateway.is_active,
                            onSelect: () => askActivate(gateway),
                          },
                          {
                            key: 'delete',
                            label: copy.rowMenu.delete,
                            danger: true,
                            disabled: gateway.is_active,
                            onSelect: () => askDelete(gateway),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </GatewayTable>

              <TruncationLine shown={gateways.length} total={query.data.total}>
                {copy.truncationTail}
              </TruncationLine>
            </>
          )
        ) : null}

        <p className="mb-0 mt-4 max-w-measure text-xs text-ink-muted">
          {copy.feeNoteLead}{' '}
          <Link to="/subscriptions" className="text-accent-text">
            {copy.feeNoteLink}
          </Link>
          .
        </p>
      </section>

      <GatewayDialog
        open={recordOpen}
        gateway={editing}
        busy={registerGateway.isPending || updateGateway.isPending}
        formError={recordError}
        onSubmit={submitRecord}
        onCancel={() => setRecordOpen(false)}
      />

      <RotateDialog
        open={rotating !== null}
        busy={rotateCredential.isPending}
        formError={rotateError}
        onCommit={commitRotation}
        onCancel={() => setRotating(null)}
      />

      <TestDialog
        open={testing !== null}
        busy={testGateway.isPending}
        result={testResult}
        lastTestedAt={testing?.last_tested_at ?? null}
        onRun={runTest}
        onClose={() => {
          setTesting(null);
          setTestResult(null);
        }}
      />

      <ConfirmDialog
        open={confirmSubject !== null}
        subject={confirmSubject}
        busy={activateGateway.isPending || deleteGateway.isPending}
        onConfirm={commitConfirm}
        onCancel={closeConfirm}
      />
    </>
  );
}

function GatewayTable({ children }: { children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.caption} minInlineSize="46rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.columns.gateway}</th>
            <th scope="col">{copy.columns.state}</th>
            <th scope="col">{copy.columns.fee}</th>
            <th scope="col">{copy.columns.callback}</th>
            <th scope="col">
              <span className="sr-only">{copy.columns.actions}</span>
            </th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}

function PlusGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.125em"
      height="1.125em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
